import { CopyToClipboard } from 'react-copy-to-clipboard'
import { handleAndToastError, toastError, toastSuccess } from 'components/Toast'
import { Platform } from 'utils/analytics/types'
import { ReactElement, useEffect, useState } from 'react'
import { getIsMobile, getWindowCenter } from 'utils/general'
import { encodeQuery } from 'utils/fetcher'
import { formatCapitalize } from 'utils/format'
import { SShareItems, SButton } from './Share.styled'
import { Icon } from 'components/icon'
import { colors } from 'utils/colors'
import { useDynamicTranslations } from 'utils/i18n/useDynamicTranslations'
import { BlockSpinner } from 'components/spinner/Spinner'
import { isFacebookMessengerAvailable } from 'utils/social'

export function ShareItems(props: {
  className?: string
  onShare?: (platform: Platform) => Promise<boolean>
  url: string
  hashtag?: string
  via?: string
  emailModal?: ReactElement
  openEmailModal?: () => void
  onFutureMessage?: () => void
  text?: string
  windowSize?: number
  isModal?: boolean
  sameSize?: boolean
  round?: boolean
}) {
  const { t, isLoading } = useDynamicTranslations('memory')
  const [browserShareState, setBrowserShareState] = useState({
    navigatorCanShare: false,
    isMobile: false,
  })
  useEffect(() => {
    setBrowserShareState({
      navigatorCanShare: !!navigator.share,
      isMobile: getIsMobile(),
    })
  }, [])
  const windowSize = props.windowSize || 600
  const { x, y } = getWindowCenter(windowSize, windowSize)
  const windowConfig = `location=yes,height=${windowSize},width=${windowSize},scrollbars=yes,status=yes,left=${x},top=${y}`
  const variant = props.round ? 'round' : 'secondary'
  const facebookUrl = encodeQuery('https://www.facebook.com/sharer/sharer.php', {
    u: props.url,
    quote: props.text,
    hashtag: `#${props.hashtag}`,
  })

  const messengerUrl = encodeQuery('https://www.facebook.com/dialog/send', {
    link: props.url,
    redirect_uri: props.url,
    app_id: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID,
  })

  const whatsappUrl = encodeQuery(
    `https://${browserShareState.isMobile ? 'api' : 'web'}.whatsapp.com/send`,
    {
      text: props.text ? `${props.text} ${props.url}` : props.url,
    }
  )

  const socialShare = async (url: string, platform: Platform) => {
    const windowReference = window.open(
      'about:blank',
      `Share via ${formatCapitalize(platform)}`,
      windowConfig
    )
    if (!windowReference) {
      toastError('allow_popup_windows')
      return
    }
    if (props.onShare) {
      await props.onShare(platform).catch((err) => {
        handleAndToastError(err)
        return
      })
    }
    windowReference.location.href = url
    windowReference.setInterval(() => {
      // Check if user has been redirected to a native app
      if (
        windowReference.location.href === 'about:blank' &&
        windowReference.document.visibilityState === 'hidden'
      )
        windowReference.close()
    }, 1000)
  }

  if (isLoading) return <BlockSpinner />

  return (
    <SShareItems className={props.className} isModal={props.isModal}>
      {props.emailModal}

      <SButton
        type="button"
        variant={variant}
        size="large"
        tone="dark"
        leftIcon={<Icon name="ColorFacebook" />}
        blurAfterClick
        onClick={() => socialShare(facebookUrl, 'facebook')}
      >
        Facebook
      </SButton>

      {props.onFutureMessage && (
        <SButton
          type="button"
          variant={variant}
          size="large"
          tone="dark"
          data-testid="share-future-message-button"
          onClick={async () => {
            if (props.onShare) {
              await props.onShare('future_message').catch((err) => {
                handleAndToastError(err)
                return
              })
            }
            await props.onFutureMessage?.()
          }}
          leftIcon={<Icon name="Send" color={colors.blue[500]} />}
        >
          {props.isModal
            ? t('memory.memory_id.share_buttons.future_message')
            : t('memory.memory_id.share_buttons.future_message_modal')}
        </SButton>
      )}

      {isFacebookMessengerAvailable() && (
        <SButton
          leftIcon={
            <img
              src="/icons/messenger@3x.png"
              width={24}
              height={24}
              css={{ objectFit: 'contain' }}
            />
          }
          type="button"
          variant={variant}
          size="large"
          tone="dark"
          blurAfterClick
          onClick={() => socialShare(messengerUrl, 'messenger')}
        >
          Messenger
        </SButton>
      )}

      <SButton
        type="button"
        variant={variant}
        size="large"
        tone="dark"
        leftIcon={<Icon name="ColorWhatsapp" />}
        blurAfterClick
        onClick={() => socialShare(whatsappUrl, 'whatsapp')}
      >
        Whatsapp
      </SButton>

      {props.openEmailModal && (
        <SButton
          type="button"
          variant={variant}
          size="large"
          tone="dark"
          onClick={props.openEmailModal}
          data-testid="share-email-button"
          leftIcon={<Icon name="Mail" color={colors.black} />}
          blurAfterClick
        >
          {t('memory.memory_id.share_buttons.email')}
        </SButton>
      )}

      <CopyToClipboard
        text={props.url}
        onCopy={async () => {
          if (props.onShare) {
            await props.onShare('copied_link').catch((err) => {
              handleAndToastError(err)
              return
            })
          }

          toastSuccess('copied_to_clipboard')
        }}
      >
        <SButton
          type="button"
          variant={variant}
          size="large"
          tone="dark"
          data-testid="share-copy-link-button"
          blurAfterClick
          leftIcon={<Icon name="Link" color={colors.blue[500]} />}
        >
          {t('memory.memory_id.share_buttons.copy_link')}
        </SButton>
      </CopyToClipboard>

      {browserShareState.navigatorCanShare && (
        <SButton
          type="button"
          variant={variant}
          size="large"
          tone="dark"
          leftIcon={<Icon name="Share" color={colors.blue[500]} />}
          blurAfterClick
          onClick={async () => {
            try {
              if (props.onShare) {
                await props.onShare('other_apps')
              }
              await navigator.share({
                url: props.url,
                title: props.text,
              })
            } catch (err) {
              if (err.message !== 'Abort due to cancellation of share.') handleAndToastError(err)
            }
          }}
        >
          {t('memory.memory_id.share_buttons.more')}
        </SButton>
      )}
    </SShareItems>
  )
}
